
import { memo, useCallback } from "react";
import { Filter } from "lucide-react";
import LoadingSpinner from "@/components/ui/loading-spinner";

interface GalleryFilterProps {
  filters: Array<{ id: string; name: string; count: number }>;
  activeFilter: string;
  isLoading: boolean;
  onFilterChange: (filterId: string) => void;
}

const GalleryFilter = memo(({ filters, activeFilter, isLoading, onFilterChange }: GalleryFilterProps) => {
  const handleFilterClick = useCallback((filterId: string) => {
    if (filterId !== activeFilter) {
      onFilterChange(filterId);
    }
  }, [activeFilter, onFilterChange]);
  
  return (
    <div className="flex flex-wrap justify-center gap-6 mb-16 relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="md" />
        </div>
      )}
      
      <div className={`flex flex-wrap justify-center gap-6 transition-opacity duration-150 ${
        isLoading ? 'opacity-50' : 'opacity-100'
      }`}>
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => handleFilterClick(filter.id)}
            disabled={isLoading}
            className={`px-6 py-3 rounded-lg font-medium transition-colors duration-150 flex items-center gap-2 disabled:cursor-not-allowed min-h-[44px] ${
              activeFilter === filter.id
                ? "bg-garage-blue text-white shadow-lg"
                : "bg-black text-white/70 hover:bg-garage-blue/20 hover:text-garage-blue border border-white/20"
            }`}
            type="button"
          >
            <Filter className="h-4 w-4" />
            {filter.name}
            <span className="text-xs bg-garage-blue/20 px-2 py-1 rounded-full">
              {filter.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
});

GalleryFilter.displayName = 'GalleryFilter';

export default GalleryFilter;
